import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { LocalBackupService } from './local-backup.service';
import { BackupAlertService } from './backup-alert.service';

export interface RecoveryGuarantee {
  /** Days of continuous, any-second recovery. Bounded by WAL retention. */
  pitrDays: number;
  /** Days where only whole backups exist — restore to their instant only. */
  snapshotOnlyDays: number;
  /** Oldest recoverable instant of any kind. */
  oldestRecoverable: string | null;
  /** Instants that can be restored to, in the snapshot-only band. */
  snapshotPoints: number;
  walSegments: number;
  /** True when a base backup is older than WAL, so it cannot be replayed. */
  hasUnreplayableBackups: boolean;
}

/**
 * Enforces retention, and computes what it actually buys.
 *
 * ── WHY THIS IS ITS OWN SERVICE ──────────────────────────────────────────────
 *
 * Retention used to be `retentionCount` applied inside a backup run, and
 * `retentionDays` applied nowhere at all — configurable, displayed, and
 * enforced by nothing. Age-based expiry has to run on its own schedule anyway
 * (a store keeps ageing when no backup is taken), so it belongs here rather
 * than as a tail-call on whichever run happened last.
 *
 * ── THE TWO TIERS, AND THE GUARD THAT MAKES THEM SAFE ────────────────────────
 *
 * WAL 5 days + backups 15 days does not mean 15 days of point-in-time recovery.
 * It means:
 *
 *     0–5 days    any second        (WAL survives, so it can be replayed)
 *     5–15 days   backup instants   (no journal; a backup restores to its own
 *                                    moment and nothing between)
 *
 * The guard that matters: WAL is useless without a base backup OLDER than the
 * instant you want. So pruning must never delete the base backup that anchors
 * the current WAL window — doing so silently destroys point-in-time recovery
 * while leaving every WAL segment sitting there looking healthy. That is the
 * single most damaging thing a retention job can do, and it is why the anchor
 * is computed and protected explicitly rather than trusted to a count.
 */
@Injectable()
export class RetentionService {
  private readonly logger = new Logger(RetentionService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly local: LocalBackupService,
    private readonly alerts: BackupAlertService
  ) {}

  /**
   * Hourly rather than daily: a store keeps ageing whether or not backups run,
   * and an hourly sweep keeps the quota honest without being expensive.
   */
  @Cron('35 * * * *')
  async sweep(): Promise<void> {
    if (!this.local.isAvailable()) return;

    try {
      const repository = await this.prisma.backupRepository.findFirst({
        where: { kind: 's3', isActive: true },
      });
      if (!repository) return;

      await this.expireWal(repository.id, repository.retentionWalDays);
      await this.expireBackups(repository.id, repository.retentionBackupDays, repository.retentionFull);
    } catch (err) {
      this.logger.warn(`retention sweep skipped: ${(err as Error).message}`);
    }
  }

  /**
   * Delete WAL older than the window.
   *
   * Deleted from R2 AND from our rows, because a row claiming a segment exists
   * is what the gap detector reads — leaving it behind would turn an expected
   * expiry into a phantom gap and a critical alert.
   */
  async expireWal(repositoryId: string, days: number): Promise<{ removed: number }> {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

    const stale = await this.prisma.walArchiveSegment.findMany({
      where: { repositoryId, status: 'uploaded', uploadedAt: { lt: cutoff } },
      orderBy: { segmentName: 'asc' },
    });
    if (!stale.length) return { removed: 0 };

    let removed = 0;
    for (const segment of stale) {
      try {
        await this.local.deleteWalObject(segment.segmentName);
        await this.prisma.walArchiveSegment.delete({ where: { id: segment.id } });
        removed++;
      } catch (err) {
        this.logger.warn(`could not expire WAL ${segment.segmentName}: ${(err as Error).message}`);
      }
    }

    if (removed) this.logger.log(`expired ${removed} WAL segment(s) older than ${days}d`);
    return { removed };
  }

  /**
   * Delete backups older than the window, protecting what recovery still needs.
   *
   * Three things survive regardless of age:
   *   1. The newest `keepAtLeast` base backups — a floor, so a quiet month
   *      cannot empty the store.
   *   2. The anchor for the current WAL window (see the class comment).
   *   3. The last remaining backup of any kind. Going from one copy to none is
   *      never the right outcome of a scheduled job.
   */
  async expireBackups(
    repositoryId: string,
    days: number,
    keepAtLeast: number
  ): Promise<{ removed: number }> {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

    const oldestWal = await this.prisma.walArchiveSegment.findFirst({
      where: { repositoryId, status: 'uploaded' },
      orderBy: { uploadedAt: 'asc' },
      select: { uploadedAt: true },
    });

    // The anchor: the newest backup at or before the start of the WAL window.
    // Everything in the WAL window replays onto this, so it outlives its age.
    const anchor = oldestWal?.uploadedAt
      ? await this.prisma.backupRun.findFirst({
          where: {
            repositoryId,
            status: 'completed',
            backupType: 'basebackup',
            finishedAt: { lte: oldestWal.uploadedAt },
          },
          orderBy: { finishedAt: 'desc' },
          select: { id: true },
        })
      : null;

    const candidates = await this.prisma.backupRun.findMany({
      where: {
        repositoryId,
        status: 'completed',
        manifestKey: { not: null },
        finishedAt: { lt: cutoff },
        ...(anchor ? { id: { not: anchor.id } } : {}),
      },
      orderBy: { finishedAt: 'desc' },
    });

    const survivors = await this.prisma.backupRun.count({
      where: { repositoryId, status: 'completed', manifestKey: { not: null } },
    });

    let removed = 0;
    for (const run of candidates) {
      if (survivors - removed <= Math.max(keepAtLeast, 1)) break;

      try {
        if (run.backupType === 'basebackup') {
          await this.local.deleteBaseBackup(run.manifestKey as string);
        } else {
          await this.local.deleteObject(run.manifestKey as string);
        }

        await this.prisma.backupRun.update({
          where: { id: run.id },
          data: { status: 'expired', deletedAt: new Date(), deletedBy: 'retention' },
        });
        removed++;
      } catch (err) {
        this.logger.warn(`could not expire ${run.manifestKey}: ${(err as Error).message}`);
      }
    }

    if (removed) this.logger.log(`expired ${removed} backup(s) older than ${days}d`);
    return { removed };
  }

  /**
   * What the current settings actually guarantee.
   *
   * Derived from what is IN the store, never from the configured numbers. A
   * retention setting is an intention; this is the outcome, and the two come
   * apart the moment a backup fails or a WAL segment goes missing. Showing the
   * intention as though it were the outcome is the specific dishonesty this
   * whole subsystem exists to prevent.
   */
  async guarantee(repositoryId: string): Promise<RecoveryGuarantee> {
    const [oldestWal, backups] = await Promise.all([
      this.prisma.walArchiveSegment.findFirst({
        where: { repositoryId, status: 'uploaded' },
        orderBy: { uploadedAt: 'asc' },
        select: { uploadedAt: true },
      }),
      this.prisma.backupRun.findMany({
        where: { repositoryId, status: 'completed', manifestKey: { not: null } },
        orderBy: { finishedAt: 'asc' },
        select: { finishedAt: true, backupType: true },
      }),
    ]);

    const walSegments = await this.prisma.walArchiveSegment.count({
      where: { repositoryId, status: 'uploaded' },
    });

    const day = 24 * 60 * 60 * 1000;
    const now = Date.now();

    // PITR reaches back only as far as BOTH a WAL run and an older base backup
    // exist. WAL with nothing to replay onto is not recoverability.
    const walStart = oldestWal?.uploadedAt?.getTime() ?? null;
    const anchor = walStart
      ? backups.filter((b) => b.backupType === 'basebackup' && (b.finishedAt?.getTime() ?? 0) <= walStart).pop()
      : undefined;

    const pitrDays = walStart && anchor ? Math.max(0, (now - walStart) / day) : 0;

    const oldestBackup = backups[0]?.finishedAt?.getTime() ?? null;
    const totalDays = oldestBackup ? (now - oldestBackup) / day : 0;

    return {
      pitrDays: Math.round(pitrDays * 10) / 10,
      snapshotOnlyDays: Math.round(Math.max(0, totalDays - pitrDays) * 10) / 10,
      oldestRecoverable: oldestBackup ? new Date(oldestBackup).toISOString() : null,
      snapshotPoints: backups.length,
      walSegments,
      // A base backup older than every WAL segment cannot be replayed forward.
      // Legitimate in the snapshot-only tier, but worth showing, because it is
      // the difference between "15 days of recovery" and "15 days of PITR".
      hasUnreplayableBackups: Boolean(walStart && backups.some((b) => (b.finishedAt?.getTime() ?? 0) < walStart)),
    };
  }
}
